import { auth } from 'boot/firebase';

export type MessageFilters = {
  limit?: number;
  timeStart?: string;
  timeEnd?: string;
  channel?: string;
  user?: string;
  message?: string;
  continueTime?: number;
  continueUuid?: string;
};

export type ChannelFilters = {
  limit?: number;
  name?: string;
  continueId?: number;
  continueTime?: string;
};

export type Channel = {
  id: number;
  name: string;
  updatedAt: string;
};

export interface APIListResponse<M, C> {
  result: M[];
  continueAfter: C;
}

export type ErrorMessage = {
  message: string;
};

export type RequestOptions = {
  url: string;
  method?: string;
  body?: FormData;
  signal?: AbortSignal;
  disableAuth?: boolean;
};

export class ValidationError extends Error {
  name = 'ValidationError';

  statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
  }
}

class API {
  baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  getURL<T>(endpoint: string, filters: T): string {
    const url = new URL(`/v1/${endpoint}`, this.baseURL);
    Object.keys(filters).forEach((key) => {
      const value = filters[key as keyof T];
      if (value) {
        url.searchParams.set(key, String(value));
      }
    });

    return url.toString();
  }

  static async makeRequest<T>({
    url,
    method,
    body,
    signal,
    disableAuth,
  }: RequestOptions) {
    const token = await auth.currentUser?.getIdToken();
    const options = {
      method,
      headers: {},
      body,
      signal,
    };

    if (!disableAuth) {
      options.headers = {
        Authorization: `Bearer ${token || ''}`,
      };
    }

    const res = await fetch(url, options);
    if (!res.ok) {
      let message: string;

      try {
        const json = await res.json() as ErrorMessage;
        message = json.message;
      } catch {
        message = res.statusText;
      }

      throw new ValidationError(message, res.status);
    }

    return await res.json() as T;
  }

  static makeRequestWithCancel<T>(options: RequestOptions) {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const controller = new AbortController();

    const request = this.makeRequest<T>({ signal: controller.signal, ...options });
    return { request, controller };
  }

  getMessages(filters: MessageFilters = {}) {
    const url = this.getURL('messages', filters);
    return API.makeRequestWithCancel<string[]>({ url, disableAuth: true });
  }

  getChannels(filters: ChannelFilters = {}) {
    const url = this.getURL('channels', filters);
    return API.makeRequestWithCancel<Channel[]>({ url });
  }

  getChannelsAutoComplete(name: string) {
    const url = this.getURL('channels/autoComplete', { name });
    return API.makeRequestWithCancel<string[]>({ url, disableAuth: true });
  }

  addChannel(name: string) {
    const url = this.getURL('channels', {});
    const body = new FormData();
    body.set('name', name);

    return API.makeRequest<Channel>({
      url,
      method: 'POST',
      body,
    });
  }

  deleteChannel(id: number) {
    const url = this.getURL(`channels/${id}`, {});
    return API.makeRequest<Channel>({
      url,
      method: 'DELETE',
    });
  }
}

const DefaultAPI = new API(process.env.API_BASE_URL || window.location.href);

export default DefaultAPI;
